import { RxCopy } from '@react-icons/all-files/rx/RxCopy'
import { RxPencil1 } from '@react-icons/all-files/rx/RxPencil1'
import throttle from 'lodash/throttle'
import * as React from 'react'

import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { toggleShortlists } from '@disco/state-management/actions/AppActions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import { sendPlaylistDownloadEventThrottled } from '@disco/utils/eventsApi'

import type { Playlist } from 'types/playlist'

import { MixpanelEventLocationContext } from 'contexts/mixpanel-event-location'
import { useDispatch, useSelector } from 'hooks/use-redux'

import { DropdownMenu, Flex } from 'components/ds'

type Props = {
  playlist: Playlist
  group:
    | {
        id: number
        group_type: string
      }
    | null
    | undefined
  behavior?: 'duplicate' | 'edit'
}

const EditPlaylist = ({
  playlist,
  group,
  behavior,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const isPlaylistCreatorOpen = useSelector(state => state.app.shortlistsOpen)
  const { featureContext } = React.useContext(MixpanelEventLocationContext)

  const latestEditPlaylist = React.useRef<() => void>(() => {})

  latestEditPlaylist.current = () => {
    if (behavior === 'duplicate') {
      dispatch(createNewShortlist(playlist, undefined, { duplicate: true }))
    } else {
      dispatch(createNewShortlist(playlist))
    }

    if (group && INBOX_TYPES.includes(group.group_type)) {
      sendPlaylistDownloadEventThrottled(
        playlist.playlist_id,
        undefined,
        group.id,
      )
    }
  }

  const editPlaylist = React.useMemo(
    () => throttle(() => latestEditPlaylist.current(), 1000),
    [],
  )

  const onSelect = () => {
    editPlaylist()

    if (behavior === 'duplicate') {
      dispatch(sendMixpanelEvent('Duplicate playlist via playlist menu'))
      if (featureContext === 'showcases') {
        dispatch(
          sendMixpanelEvent(
            'Manage Artist modal - showcase - Duplicate playlist',
          ),
        )
      }
    } else {
      dispatch(sendMixpanelEvent('Edit playlist via playlist menu'))
      if (featureContext === 'showcases') {
        dispatch(
          sendMixpanelEvent('Manage Artist modal - showcase - Edit playlist'),
        )
      }
    }

    if (!isPlaylistCreatorOpen) {
      const isSearchPage = Boolean(window.location.pathname.includes('/search'))
      const currentLocation = isSearchPage ? 'Search' : 'Explorer'
      const actionType =
        behavior === 'duplicate' ? 'Duplicate Playlist' : 'Edit Playlist'
      dispatch(toggleShortlists(true, currentLocation, actionType))
    }
  }

  if (behavior === 'duplicate') {
    return (
      <DropdownMenu.Item onSelect={onSelect}>
        <Flex align="center" gap="2">
          <RxCopy />
          Duplicate playlist
        </Flex>
      </DropdownMenu.Item>
    )
  }

  return (
    <DropdownMenu.Item onSelect={onSelect}>
      <Flex align="center" gap="2">
        <RxPencil1 />
        Edit playlist
      </Flex>
    </DropdownMenu.Item>
  )
}

export default EditPlaylist
